import cx from "clsx";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import Select, {
  Option,
} from "kreate-common/modules/kreate-ui/components/Select";
import { GetUserList$Params } from "kreate-server/commands/GetUserList/typing";
import { useRouter } from "next/router";
import React from "react";

import PanelSearch from "../PageExploreArtworks/components/PanelSearch";

import LazyUserList from "./components/LazyUserList";
import styles from "./index.module.scss";

import { useDebounce } from "@/modules/common-hooks/useDebounce";
import { range } from "@/modules/common-utils/range";
import { EM_DASH } from "@/modules/common-utils/unicode";
import Checkbox from "@/modules/kreate-components/components/Checkbox";
import PageLayout from "@/modules/kreate-components/components/PageLayout";

const ITEMS_PER_PAGE = 10;
const NUM_MAX_ARTWORKS = 5;

type Props = {
  className?: string;
  style?: React.CSSProperties;
};

type OrderBy = Exclude<GetUserList$Params["orderBy"], undefined>;

const ORDER_BY__OPTIONS: Option<OrderBy>[] = [
  { value: "popular", label: "Most Loved" },
  { value: "new", label: "Newest" },
];

export default function PageExploreArtists({ className, style }: Props) {
  const router = useRouter();
  const [pageCount, setPageCount] = React.useState(1);
  const [endOfResults, setEndOfResults] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState(
    typeof router.query.q === "string" ? router.query.q : ""
  );
  const [debouncedSearchInput] = useDebounce(searchInput, {
    delay: 500,
  });
  const [totalNumResults, setTotalNumResults] = React.useState<
    number | null | undefined
  >(null);
  const [orderBy, setOrderBy] = React.useState<OrderBy>("popular");
  const [isVerified, setIsVerified] = React.useState(false);

  React.useEffect(() => {
    setPageCount(1);
    setEndOfResults(false);
  }, [debouncedSearchInput, orderBy, isVerified]);

  return (
    <div className={cx(styles.container, className)} style={style}>
      <PageLayout
        style={{ minHeight: "120vh" }}
        title="Explore Artists | Kreate"
        background="light"
        hideSearchBarOnNavbar
        shouldUseNavbarV3
      >
        <PageLayout.Section size="sm" marginTop="48px" marginBottom="48px">
          <PanelSearch
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            state="artists"
          />
        </PageLayout.Section>
        <PageLayout.Section marginTop="48px" marginBottom="36px">
          <Flex.Row
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap="12px"
          >
            <h1 className={styles.title}>
              {totalNumResults != null ? `${totalNumResults} Artists` : EM_DASH}
            </h1>
            <Flex.Row alignItems="center" gap="12px">
              <Checkbox
                value={isVerified}
                onChange={setIsVerified}
                label="Verified Artists"
              />
              <Divider.Vertical style={{ margin: "12px 0" }} />
              <Select
                options={ORDER_BY__OPTIONS}
                value={orderBy}
                onChange={setOrderBy}
              />
            </Flex.Row>
          </Flex.Row>
        </PageLayout.Section>
        <PageLayout.Divider style={{ marginTop: "24px" }} />
        <PageLayout.Section
          key={`${debouncedSearchInput} ${orderBy} ${isVerified}`}
        >
          {range(pageCount).map((index) => (
            <LazyUserList
              key={index}
              params={{
                searchQuery: debouncedSearchInput,
                offset: index * ITEMS_PER_PAGE,
                isVerified: isVerified || undefined,
                limit: ITEMS_PER_PAGE,
                numMaxArtworks: NUM_MAX_ARTWORKS,
                orderBy,
              }}
              onLoad={({ maybeHasMore, totalNumResults }) => {
                setTotalNumResults(totalNumResults);
                if (maybeHasMore) {
                  setPageCount((value) => Math.max(value, index + 2));
                } else {
                  setEndOfResults(true);
                }
              }}
            />
          ))}
          {endOfResults ? (
            <div className={styles.textEndOfResults}>{"End of results."}</div>
          ) : null}
        </PageLayout.Section>
      </PageLayout>
    </div>
  );
}
